import createHttp from '@/config/http';
import type {
	CommissionableProduct,
	CreateReturnOrderPayload,
	OrderReturn,
	OrderReturnsResponse,
	ReturnOrderDetails,
} from '@/types/orderReturns';
import type { PatientClaimRow } from '@/types/patient';
import type { ResponseMetaData } from '@/types/service';

// localhost:3002 (Orders microservice port)
const http = createHttp(import.meta.env.VITE_ORDERS_URL);

export const getOrderReturns = async (
	page = 1,
	limit = 15,
	columnsFilters = '[]',
	search = '',
	status: string | number = '',
	globalFilters = '[]',
	sort = '',
): Promise<{
	items: OrderReturn[];
	meta: ResponseMetaData;
}> => {
	const response = await http.get<OrderReturnsResponse>('/orders/returns', {
		params: {
			page,
			limit,
			columnsFilters,
			search,
			status,
			globalFilters,
			sort,
		},
	});
	return {
		items: response.data.data.items,
		meta: {
			...response.data.data.metaData,
			itemsPerPage: response.data.data.metaData.recordsPerPage,
		},
	};
};

export const getReturnProducts = async (
	page = 1,
	limit = 15,
	globalFilters = '[]',
): Promise<{
	items: {
		productName: string;
		qCode: string;
		tissueId: string;
		inventoryId: string;
	}[];
	meta: ResponseMetaData;
}> => {
	const response = await http.get('/orders/returns/products', {
		params: {
			page,
			limit,
			globalFilters,
		},
	});
	return {
		items: response.data.data.items,
		meta: {
			...response.data.data.metaData,
			itemsPerPage: response.data.data.metaData.recordsPerPage,
		},
	};
};

export const getClaimsByPatient = async (
	id: string,
): Promise<PatientClaimRow[]> => {
	const response = await http.get<{ data: any[] }>(`/orders/history/${id}`);
	return response.data.data || [];
};

export const getCommissionableProducts = async (): Promise<
	CommissionableProduct[]
> => {
	const response = await http.get<{ data: CommissionableProduct[] }>(
		'/orders/products/commissionable',
	);
	return response.data.data;
};

export const createReturnOrder = async (payload: CreateReturnOrderPayload) => {
	const response = await http.post('/orders/create-return-request', payload);
	return response.data;
};

export const getReturnOrderById = async (
	id: string,
): Promise<{ data: ReturnOrderDetails }> => {
	const response = await http.get<{ data: ReturnOrderDetails }>(
		`/orders/return-orders/${id}`,
	);
	return response.data;
};
